import React, { useState, useEffect } from 'react';
import { Link } from '@reach/router';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import Wrapper from '../../elements/wrapper';
import SEO from '../../seo';
import ColorModal from '../branded/color-modal';
import { media } from '../../helpers';

import TrashIcon from '../../../images/icons/trash.inline.svg';

const colorsSwatches = [
    {
        title: 'Primary Color',
        slug: 'primary_color',
    },
    {
        title: 'Secondary Color',
        slug: 'secondary_color',
    },
    {
        title: 'Accent',
        slug: 'accent_1',
    },
    {
        title: 'Accent',
        slug: 'accent_2',
    },
    {
        title: 'Accent',
        slug: 'accent_3',
    },
];

const StepThreeContainer = styled.section`
    > .title-container {
        text-align: center;
        margin-bottom: 80px;

        h4 {
            font-size: 1.1rem;
            font-style: italic;
            color: #000;
        }

        h1 {
            font-size: 3rem;
            color: #000;
            font-family: neue-kabel, sans-serif;
            font-weight: 800;
            margin: 0 0 30px;
        }

        p {
            font-size: 1.1rem;
            max-width: 950px;
            margin: 0 auto;
            line-height: 1.5;
        }

        .progress-bar-container {
            display: flex;
            max-width: 550px;
            margin: 0 auto 40px;
            border-radius: 20px;
            border: 2px solid #000;
            .block {
                height: 20px;
                width: 300px;

                border-left: 2px solid #000;

                &:first-child {
                    border-top-left-radius: 20px;
                    border-bottom-left-radius: 20px;
                }
                &:last-child {
                    border-top-right-radius: 20px;
                    border-bottom-right-radius: 20px;
                }

                &.filled {
                    background-color: #f74d33;
                }
            }
        }
    }
    .color-select-wrapper {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 40px;
        ${media.large`max-width: 60vw;`}
        ${media.laptop`max-width: 70vw;`}

        .item-container {
            flex: 0 0 50%;
            margin-bottom: 20px;
            ${media.small`flex: 0 0 33.33%;`}
            ${media.laptop`flex: 0 0 20%; margin-bottom: 0px;`}
            > .inner-wrap {
                max-width: 90%;
                margin: 0 auto;
                .color-container {
                    /* background-color: #e5e5e5; */
                    border-radius: 10px;
                    border: 2px solid #f2f0eb;
                    .small-title-container {
                        h4 {
                            margin: 0 0 15px;
                        }
                    }
                    > .inner-wrap {
                        padding: 20px;
                        .color-swatch {
                            width: 100%;
                            height: 150px;
                            background-color: #fff;
                            margin: 0 auto 15px;
                            border: 2px solid #f2f0eb;
                        }
                        button {
                            background-color: transparent;
                            border: none;
                            padding: 0;
                            text-decoration: underline;
                            color: #702716;
                        }
                        .content-container {
                            display: flex;

                            .btn-container {
                                flex: 1;

                                &:last-child {
                                    text-align: right;

                                    svg {
                                        position: relative;
                                        top: 3px;
                                        path {
                                            fill: #702716;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    .footer-nav {
        margin-bottom: 40px;
        display: flex;
        align-items: center;
        ${media.large`max-width: 68vw; margin-bottom: 60px;`}
        .btn-container {
            flex: 1;
            text-align: right;
            a {
                max-width: 170px;
                width: 100%;
                padding: 20px 0;
                background-color: #f74d33;
                color: #fff;
                text-decoration: none;
                border: none;
                font-weight: bold;
                border-radius: 4px;
                text-align: center;
                display: inline-block;
                font-family: neue-kabel, sans-serif;
                font-weight: 800;
            }
        }
        .link-container {
            margin-right: 100px;
            flex: 1;

            a {
                color: #000;
                font-weight: bold;
                text-decoration: none;
            }
        }
    }

    .info-tab-container {
        .info-tab {
            max-width: 550px;
            background-color: #f2f0eb;
            box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.12);
            border-radius: 5px;
            padding: 40px;
            position: relative;
            margin: 0 auto 40px;
            .icon {
                position: absolute;
                top: -15px;
                left: -10px;
                padding: 10px 20px;
                background-color: #f74d33;
                border-radius: 50%;
                font-weight: bold;
                color: #fff;
                font-size: 2rem;
                ${media.large`left: -20px;`}
            }
            p {
                font-weight: bold;
                text-align: left;
                margin: 0;
                color: #000;
            }
        }
    }
`;

// Form
const StepThree = ({ brandedSettings, setBrandedSettings, brandedInit }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentColorSlug, setCurrentColorSlug] = useState('');

    function toggleModalOpen(slug) {
        setCurrentColorSlug(slug);
        setIsModalOpen(true);
    }
    function toggleModalClose() {
        setIsModalOpen(false);
    }

    function addColor(colorName, colorCode) {
        let newObject = brandedSettings.product_colors;
        let newColor = {
            title: colorName,
            code: colorCode,
        };
        newObject[currentColorSlug] = newColor;
        setBrandedSettings({ ...brandedSettings, product_colors: newObject });
        setIsModalOpen(false);
    }

    function clearColor(slug) {
        let newObject = brandedSettings.product_colors;
        newObject[slug] = '';
        setBrandedSettings({ ...brandedSettings, product_colors: newObject });
    }

    useEffect(() => {
        brandedInit();
    }, []);

    return (
        <StepThreeContainer>
            <SEO title="Step 3 –– Branded" />
            <Wrapper className="title-container">
                <h4>Step 3</h4>
                <div className="progress-bar-container">
                    <div className="block filled"></div>
                    <div className="block filled"></div>
                    <div className="block filled"></div>
                    <div className="block"></div>
                </div>
                <h1>Select your colors</h1>
                <p>
                    Select the colors that our design team will use to create
                    your proof. Start by choosing the primary color for the
                    pattern (this color will be the most prominent in the
                    design), and then choose your secondary and accent colors.
                </p>
            </Wrapper>

            <Wrapper className="color-select-wrapper">
                {colorsSwatches &&
                    colorsSwatches.map((swatch, index) => {
                        let isColorSelected =
                            brandedSettings.product_colors[swatch.slug] !== '';

                        return (
                            <div className="item-container">
                                <div className="inner-wrap">
                                    <div className="color-container">
                                        <div className="inner-wrap">
                                            <div className="small-title-container">
                                                <h4>{swatch.title}</h4>
                                            </div>
                                            <div className="color-select-container">
                                                <div
                                                    className="color-swatch"
                                                    style={
                                                        isColorSelected
                                                            ? {
                                                                  backgroundColor:
                                                                      brandedSettings
                                                                          .product_colors[
                                                                          swatch
                                                                              .slug
                                                                      ].code,
                                                              }
                                                            : {}
                                                    }
                                                ></div>
                                                <div className="content-container">
                                                    <div className="btn-container">
                                                        <button
                                                            onClick={() =>
                                                                toggleModalOpen(
                                                                    swatch.slug
                                                                )
                                                            }
                                                        >
                                                            {isColorSelected
                                                                ? 'Edit'
                                                                : 'Select'}
                                                        </button>
                                                    </div>
                                                    <div className="btn-container">
                                                        {isColorSelected ? (
                                                            <button
                                                                onClick={() =>
                                                                    clearColor(
                                                                        swatch.slug
                                                                    )
                                                                }
                                                            >
                                                                <TrashIcon />
                                                            </button>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
            </Wrapper>
            <Wrapper className="footer-nav">
                <div className="link-container">
                    <Link to="/branded/step-2">{'< '}Back</Link>
                </div>
                <div className="btn-container">
                    <Link to="/branded/confirmation">Next Step</Link>
                </div>
            </Wrapper>
            <Wrapper className="info-tab-container">
                <div className="info-tab">
                    <div className="icon">!</div>
                    <p>
                        The more color information you provide the better. We
                        can also easily change colors at any point in the
                        process.
                    </p>
                </div>
            </Wrapper>
            <AnimatePresence>
                {isModalOpen && (
                    <ColorModal
                        addColor={addColor}
                        toggleModalClose={toggleModalClose}
                    />
                )}
            </AnimatePresence>
            {/* <ColorModal /> */}
        </StepThreeContainer>
    );
};

export default StepThree;
